import backEnd from "../../constants/backEnd";
import endpoints from "../../constants/endpoints";
import { postHistoric } from "../postHistoric";
import formatDate from "../../utils/formatDate";
import { getCSRF } from "../getCSRF";
import Cookies from 'js-cookie'

/**
 * Lê um stream SSE (Server-Sent Events) via ReadableStream e chama onDelta para cada delta de texto.
 */
async function consumeSSEStream(response, { onDelta, onEvent, onDone, submitRef, signal }) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  const flushEventBlock = async (block) => {
    // Um bloco SSE pode ter varias linhas: event:, data:, comentários, etc.
    const lines = block.split("\n");
    let eventName = null;
    const dataParts = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith(":")) continue; // comentário ou vazio
      if (trimmed.startsWith("event:")) {
        eventName = trimmed.slice(6).trim();
      } else if (trimmed.startsWith("data:")) {
        dataParts.push(trimmed.slice(5).trim());
      }
    }

    const dataStr = dataParts.join("\n");
    if (!dataStr) return;

    if (dataStr === "[DONE]") {
      onDone && onDone();
      return "DONE";
    }

    try {
      const payload = JSON.parse(dataStr);

      // Eventos específicos
      if (eventName && onEvent) {
        onEvent(eventName, payload);
      }

      // Deltas de texto
      if (payload?.type === "delta" && typeof payload.content === "string") {
        onDelta && onDelta(payload.content);
      }

      // Erros
      if (payload?.type === "error") {
        console.error("SSE error:", payload);
      }
    } catch (e) {
      // Pode haver blocos não JSON (ex. logs), ignore
      console.warn("SSE non-JSON data:", dataStr);
    }
  };

  try {
    while (true) {
      if (signal?.aborted || (submitRef && !submitRef.current)) {
        // Cancelamento solicitado
        try {
          reader.cancel();
        } catch (_) {}
        break;
      }

      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE delimita eventos por \n\n
      let sepIndex;
      while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
        const eventChunk = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        const res = await flushEventBlock(eventChunk);
        if (res === "DONE") {
          // Encerrado pelo servidor
          try {
            reader.cancel();
          } catch (_) {}
          return;
        }
      }
    }

    // Processa o que sobrar (caso sem \n\n final)
    if (buffer.trim()) {
      await flushEventBlock(buffer);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (_) {}
  }
}

export async function completions(
  api, // mantido para compatibilidade; usado no histórico
  messages,
  config,
  send,
  updateHistoric,
  setReturnText,
  setSubmit,
  chat = false,
  submit = true
) {
  const submitRef = { current: submit }; // referência mutável para permitir cancelamento
  const controller = new AbortController();

  // Se quiser cancelar de fora, você pode expor controller.abort()
  // Ex.: return { abort: () => controller.abort() }

  try {
    const csrf = await getCSRF();
    const model = chat ? "gpt-4o-mini" : config.model;

    const payload = {
      model,
      messages,
      temperature: config.temperature,
      max_tokens: config.max_tokens,
      top_p: config.top_p,
      frequency_penalty: config.frequency_penalty,
      presence_penalty: config.presence_penalty,
      user: `user${config.user}`,
    };


    const session = Cookies.get('airton@data') && JSON.parse(Cookies.get('airton@data'))

    const res = await fetch(`${backEnd.url}${endpoints.openai.completions}`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${session.token}`,
        "Content-Type": "application/json",
        "X-CSRFToken": csrf?.csrfToken,
        "Accept": "text/event-stream, application/json;q=0.9, /;q=0.1",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
      credentials: "include", // garante envio de cookies (CSRF, session) se necessário
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} - ${errText || res.statusText}`);
    }

    let finalText = "";
    const initialText = "";

    await consumeSSEStream(res, {
      signal: controller.signal,
      submitRef,
      onEvent: (evt, data) => {
        // Ex.: evt === "meta" com { max_tokens: ... }
        // console.log("SSE event:", evt, data);
      },
      onDelta: (deltaText) => {
        if (!submitRef.current) {
          controller.abort();
          return;
        }
        if (finalText === "") {
          finalText = initialText + deltaText;
        } else {
          finalText += deltaText;
        }
        setReturnText(finalText);
      },
      onDone: () => {
        // fim do stream
      },
    });

    // Histórico
    let historic = {
      send: send,
      text: JSON.stringify(messages),
      result: finalText,
      date: formatDate(new Date()),
      action: api, // mantendo compatível com sua estrutura atual
    };
    if (chat) {
      historic = { ...historic, model: "gpt-4o-mini" };
    }

    await postHistoric(historic);
    updateHistoric.set(finalText);
  } catch (err) {
    if (err?.name === "AbortError") {
      // cancelado
      // console.log("Streaming aborted");
    } else {
      console.error("Erro no streaming:", err);
      // Você pode exibir um erro para o usuário aqui
    }
  } finally {
    setSubmit(false);
  }
}